import { useEffect, useState } from "react";
import { Wrench, Save, ToggleRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function AdminMaintenance() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/maintenance");
      setCfg({ enabled: !!r.data.enabled, message: r.data.message || "" });
    } catch {
      toast.error("Could not load maintenance settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/maintenance", patch);
      setCfg({ enabled: !!r.data.enabled, message: r.data.message || "" });
      toast.success(patch.enabled === true ? "Maintenance ON — site hidden from users" : patch.enabled === false ? "Maintenance OFF — site is live again" : "Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-12 text-center font-bold">Loading…</div>;

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-3xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin · Site</p>
        <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-3">
          <Wrench className="w-8 h-8" strokeWidth={2.5}/> Maintenance mode
        </h1>
        <p className="text-base font-medium text-slate-600 mt-2">
          When ON, all non-admin users see an "Under construction" page and every API call (except auth + admin) returns 503. Admins keep full access.
        </p>
      </header>

      <div className={`nb-card p-6 mb-6 ${cfg.enabled ? "bg-pink-200" : "bg-yellow-100"}`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest">Status</div>
            <div className="font-display text-2xl font-black mt-1">
              {cfg.enabled ? "ON — site hidden from users" : "OFF — site is live"}
            </div>
          </div>
          <button
            data-testid="admin-maintenance-toggle"
            onClick={() => save({ enabled: !cfg.enabled })}
            disabled={busy}
            className={`nb-btn-primary inline-flex items-center gap-2 ${cfg.enabled ? "!bg-emerald-300" : "!bg-pink-300"}`}
          >
            <ToggleRight className="w-5 h-5" strokeWidth={2.5}/>
            {cfg.enabled ? "Turn OFF" : "Turn ON"}
          </button>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); save({ message: cfg.message }); }}
        className="nb-card p-6 space-y-5"
      >
        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Message shown to users</label>
          <textarea
            data-testid="admin-maintenance-message"
            value={cfg.message}
            onChange={(e) => setCfg({ ...cfg, message: e.target.value })}
            rows={4}
            maxLength={400}
            className="nb-input"
            placeholder="We're making things better behind the scenes. Sorry for any inconvenience — please check back soon."
          />
          <p className="text-xs text-slate-500 mt-2">{(cfg.message || "").length} / 400</p>
        </div>
        <button type="submit" disabled={busy} data-testid="admin-maintenance-save" className="nb-btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" strokeWidth={2.5}/> Save message
        </button>
      </form>
    </div>
  );
}
